/**
 * Machining charge engine for rod/bar/tube cut lists. Every cross-cut on a
 * length of stock sweeps through its entire cross-section exactly once, so
 * (unlike the sheet tool's per-cut-length model) each cut is priced directly
 * from the cross-sectional area: area^exponent, clamped between minCharge
 * and maxCharge - then multiplied by the total number of pieces cut,
 * independent of how those pieces get packed onto individual stock lengths.
 *
 * Reuses window.MachiningCharge.pickMachiningCartItems (assets/machining-charge.js)
 * to turn the computed charge into Machining Charge product line items, so
 * both tools share the exact same price-ladder catalog.
 */
(function (window) {
  'use strict';

  /**
   * Extracts every number found in a cross-section option value, e.g.
   * "5" -> [5], "9.5 x 9.5" -> [9.5, 9.5], "4 x 4 x 5.7" -> [4, 4, 5.7].
   */
  function extractNumbers(value) {
    if (!value) return [];
    var matches = String(value).match(/[0-9]+(\.[0-9]+)?/g);
    return matches ? matches.map(Number) : [];
  }

  /**
   * Triangle area from its three side lengths (Heron's formula).
   */
  function triangleArea(a, b, c) {
    var s = (a + b + c) / 2;
    var value = s * (s - a) * (s - b) * (s - c);
    return value > 0 ? Math.sqrt(value) : 0;
  }

  /**
   * Cross-sectional area from a raw cross-section option value, inferring
   * the shape from how many numbers it contains:
   *   1 number  -> round (diameter/outside diameter): true circular area
   *   2 numbers -> square/rectangular (e.g. external dimensions): W x H
   *   3 numbers -> triangular, given as three side lengths
   */
  function crossSectionArea(crossSectionValue) {
    var numbers = extractNumbers(crossSectionValue);

    if (numbers.length === 1) {
      var diameter = numbers[0];
      return (Math.PI / 4) * diameter * diameter;
    }
    if (numbers.length === 2) {
      return numbers[0] * numbers[1];
    }
    if (numbers.length === 3) {
      return triangleArea(numbers[0], numbers[1], numbers[2]);
    }
    return 0;
  }

  /**
   * @param {string} crossSectionValue - raw variant option value (diameter,
   *   outside diameter, or "W x H" / "A x B x C" dimensions)
   * @param {number} totalPieces - total quantity of pieces being cut, across
   *   all stock lengths
   * @param {{exponent:number, minCharge:number, maxCharge:number}} pricing -
   *   per-cut charge is area^exponent, clamped to [minCharge, maxCharge]
   * @param {{pence:number, variantId:number}[]} table - shared machining
   *   charge price-ladder table
   */
  function computeRodMachiningCharge(crossSectionValue, totalPieces, pricing, table) {
    var area = crossSectionArea(crossSectionValue);
    if (!(area > 0) || !(totalPieces > 0)) return null;

    var chargePerCut = Math.min(Math.max(Math.pow(area, pricing.exponent), pricing.minCharge), pricing.maxCharge);
    var chargeGBP = chargePerCut * totalPieces;
    if (!isFinite(chargeGBP) || chargeGBP < 0) return null;

    return {
      chargeGBP: chargeGBP,
      chargeGBPRounded: window.MachiningCharge.roundChargeGBP(chargeGBP),
      area: area,
      chargePerCut: chargePerCut,
      totalPieces: totalPieces,
      cartItems: window.MachiningCharge.pickMachiningCartItems(chargeGBP, table),
    };
  }

  window.RodMachiningCharge = {
    extractNumbers: extractNumbers,
    triangleArea: triangleArea,
    crossSectionArea: crossSectionArea,
    computeRodMachiningCharge: computeRodMachiningCharge,
  };
})(window);
